import { Country } from "@utils/constants/localisation";
import {
  IMSTokenParams,
  getWithIDToken,
  postWithIdToken,
} from "@utils/http/MSAuthAPI";

// Enum for different pricing record status based on the Zoho Sign's quote status
export enum PricingRecordStatus {
  QUOTE_SENT = 'Quote Sent',
  QUOTE_RECALLED = 'Quote Recalled',
  QUOTE_EXPIRED = 'Quote Expired',
  QUOTE_SIGNED = 'Quote Signed',
  QUOTE_DECLINED = 'Quote Declined'
}

// Interface for query parameters
export interface IMasterPricingSelectionParams {
  country: string;
  state: string;
  plan: string;
  vehicle_condition: string;
  term_length?: string | null;
  make?: string | null;
  model?: string | null;
  model_year?: string | null;
  weekly_distance_allowance_id?: string | null;  // TODO: should be optional?
  initial_contribution_id?: string | null;
  balloon_payment_id?: string | null;
  // Per-vehicle targeting value; null until selected — the server treats an
  // absent specifier as '0' ("not vehicle-specific") when generating a record.
  vehicle_specifier?: string | null;
}

// Interfaces for backend response
export interface IFilterOption {
  id: string;
  description: string;
}

export interface IFilterOptions {
  state: IFilterOption[];
  plan: IFilterOption[];
  vehicle_condition: IFilterOption[];
  term_length: IFilterOption[];
  make: IFilterOption[];
  model: IFilterOption[];
  model_year: IFilterOption[];
  distance_allowance: IFilterOption[];
  initial_contribution: IFilterOption[];
  balloon_payment: IFilterOption[];
  vehicle_specifier?: IFilterOption[];
}

export interface IFilter {
  country: string;
  state: string;
  plan: string;
  vehicle_condition: string;
  term_length: string | null;
  make: string | null;
  model: string | null;
  model_year: string | null;
  distance_allowance: string | null;
  initial_contribution_id: string | null;
  balloon_payment_id: string | null;
  vehicle_specifier?: string | null;
}

export interface IPricingRecordLineItem {
  pricing_template_line_item_id: number;
  chargebee_item_key: string;
  included: boolean;
  price_including_gst: number;
  price_excluding_gst: number;
  pricing_template_section_id: number;
  line_item_label_code: string;
  line_item_label_name: string;
  line_item_label_value: string | null;
  line_item_sorting_sequence: number;
  chargebee_component: string;
  chargebee_item_type: string;
  chargebee_item_name: string;
}

export interface IPricingRecordSection {
  pricing_template_section_id: number;
  section_label: string;
  section_sorting_sequence: number;
}

export interface IPricingRecord {
  country: string;
  state: string;
  status: string;
  plan: string;
  vehicle_condition: string;
  term_length: string;
  term_length_in_years: number;
  make: string;
  model: string;
  model_year: string;
  vehicle_colour: string | null;
  vehicle_delivery_eta: string | null;
  weekly_distance_allowance: number;
  weekly_distance_allowance_id: string;
  initial_contribution_id: string | null;
  initial_contribution: number | null;
  balloon_payment_id: string | null;
  balloon_payment: number | null;
  // '0' = not vehicle-specific; anything else targets a single vehicle (POFO)
  vehicle_specifier?: string | null;
  excess_distance_cost: number;
  setup_fee_including_gst: number;
  pricing_record_line_items: IPricingRecordLineItem[];
  pricing_record_sections: IPricingRecordSection[];
  zoho_contact: any;
  zoho_potential: any;
  zoho_sign_quote_detail: any;
  payout_waiver_opt_out_evidence?: string;
  payout_waiver_opt_out_evidence_checked_by?: string;
  loss_and_damage_opt_out_evidence?: string | null;
  loss_and_damage_opt_out_evidence_checked_by?: string | null;
  reference_values?: any;
  component_lists?: any;
  created_datetime?: string | null ;

  // The following fields are used only for pre-owned vehicles
  vehicle_registration: string | null;
  vehicle_driveaway_price: number | null;
  term_reduction: number | null;
  splend_servicing_limit: number;
  replacement_tyres: number;
  replacement_brake_pad_set: number;
  replacement_or_machining_of_brake_discs: number;

  splend_initiative?: string | null;
  minimum_period_weeks?: number | null;
  trip_count_reviewed_by?: string | null;

  servicing_and_consumables_opt_out_applicable?: boolean | null;
  payout_protection_exempt?: boolean | null;
  servicing_and_consumables_exempt?: boolean | null;
}

export interface IMasterPricingPlan {
  filter: IFilter;
  filter_option: IFilterOptions;
  pricing_record: IPricingRecord;
}

// Sold-vehicle indicator (TP-6846): distinct-customer counts for a physical vehicle,
// keyed on vehicle_specifier. Derived from quote data only (no Fleetio call).
export interface IVehicleQuoteSummary {
  vehicle_specifier: string;
  // Distinct customers who have been *sent* a quote for this vehicle.
  quoted_customer_count: number;
  // Distinct customers whose quote for this vehicle became a subscription (onboarded).
  // Always <= quoted_customer_count.
  onboarded_customer_count: number;
}

export interface IPricingDetail {
  pricing_record: IPricingRecord;
}

// Declare interface for API services
interface IQuoteBuilderService {
  getMasterPricingPlan(
    qParams: IMasterPricingSelectionParams,
    IDTokenParams: IMSTokenParams,
    zohoPotentialId?: string
  ): Promise<IMasterPricingPlan>;

  getPricingRecordUsingPotential(
    qParams: object,
    country: Country,
    zohoPotentialId: string,
    IDTokenParams: IMSTokenParams
  ): Promise<any>;

  postPricingRecord(
    country: Country,
    zohoPotentialId: string,
    payload: any,
    IDTokenParams: IMSTokenParams
  ): Promise<any>;

  getVehicleQuoteSummary(
    country: string,
    vehicleSpecifier: string,
    IDTokenParams: IMSTokenParams
  ): Promise<IVehicleQuoteSummary>;
}

export const QuoteBuilderService: IQuoteBuilderService = {
  async getMasterPricingPlan(
    qParams: IMasterPricingSelectionParams,
    IDTokenParams: IMSTokenParams,
    zohoPotentialId?: string
  ): Promise<IMasterPricingPlan> {
    // Real API Call
    const resp = await getWithIDToken("v1/master-pricing/plan", IDTokenParams, {
      ...qParams,
      ...(zohoPotentialId ? { zoho_potential_id: zohoPotentialId } : {})
      //country,
    });
    console.debug("Master pricing plan fetched:", { data: resp.data });
    return resp.data;
  },

  async getPricingRecordUsingPotential(
    qParams: object,
    country: Country,
    zohoPotentialId: string,
    IDTokenParams: IMSTokenParams
  ): Promise<IPricingDetail> {
    // Real API Call
    const resp = await getWithIDToken(
      `v1/pricing-record/potential/${country}/${zohoPotentialId}`,
      IDTokenParams,
      {
        ...qParams,
      },
    );
    console.debug("Pricing Record fetched:", { country, data: resp.data });
    return resp.data;
  },

  async postPricingRecord(
    country: Country,
    zohoPotentialId: string,
    payload: IPricingDetail,
    IDTokenParams: IMSTokenParams
  ): Promise<any> {
    const resp = await postWithIdToken(
      `v1/pricing-record/potential/${country}/${zohoPotentialId}`,
      IDTokenParams,
      {},
      { ...payload }
    );
    console.debug("Pricing Record saved:", {
      country,
      zohoPotentialId,
      data: resp.data,
    });
    return resp.data;
  },

  async getVehicleQuoteSummary(
    country: string,
    vehicleSpecifier: string,
    IDTokenParams: IMSTokenParams
  ): Promise<IVehicleQuoteSummary> {
    const resp = await getWithIDToken(
      `v1/pricing-record/vehicle-summary/${country}/${vehicleSpecifier}`,
      IDTokenParams
    );
    console.debug("Vehicle quote summary fetched:", { country, vehicleSpecifier, data: resp.data });
    return resp.data;
  },
};
